import { useCallback, useRef, useState } from 'react';

import { isNil } from 'ramda';
import { Field, useForm, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import Preview from 'components/Pages/Admin/shared/Preview';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Modal from 'components/shared/Modal';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import { SafariContentOption } from 'components/shared/Select/options/SafariContent';
import { isLoginAsSelector } from 'store/auth/selectors';
import { accountIdSelector, isAgentSelector, isOwnerSelector } from 'store/user/selectors';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';
import { ERROR_MESSAGES } from 'utils/yup';

function validateRequired({ message } = {}) {
  return (value) => {
    if (!value) return message ?? ERROR_MESSAGES.required;
    return undefined;
  };
}

const decoratedAxiosGet = axiosGetAutoCancelDecorator();
function MapToMasterProperty({ readOnly, required, creatable }) {
  const form = useForm();
  const isAgent = useSelector(isAgentSelector);
  const accountId = useSelector(accountIdSelector);
  const selectRef = useRef();
  const [previewModalState, setPreviewModalState] = useState({ isOpen: false, pageId: null });

  const handleClosePreview = () => {
    setPreviewModalState({ isOpen: false });
    if (selectRef.current) {
      selectRef.current.focus();
    }
  };

  const loadOptions = async (q) => {
    if (readOnly) {
      const formState = form.getState();
      return [
        {
          label: formState.initialValues.masterPropertyName,
          value: formState.initialValues.masterPropertyId,
        },
      ];
    }

    const response = await decoratedAxiosGet(`/acc/${accountId}/pages/dropdown`, {
      params: { q, limit: 100, master_properties: true, order_by: 'handle', countless: '1' },
    });

    const options = response.data.data.map((item) => ({
      value: item.id,
      label: item.handle,
      optionActionAvailable: item.currentStep === 'page',
      showContentIndicator: isAgent && (item.systemWide || item?.shared),
      shared: item?.shared,
    }));

    return options;
  };

  const OptionMemoized = useCallback(
    (props) => (
      <SafariContentOption
        hasPreview={props.data.optionActionAvailable}
        onPreview={() => setPreviewModalState({ isOpen: true, pageId: props.data.value })}
        hasContentIndicator={props.data.showContentIndicator}
        shared={props.data?.shared}
        {...props}
      />
    ),
    [],
  );

  return (
    <>
      <Field
        name="masterPropertyId"
        component={FormGroupAdapter}
        label={`Link to Master Property Page${required ? '*' : ''}`}
        customComponent={Select}
        readOnly={readOnly}
        isClearable
        placeholder="Type property name here..."
        async
        loadOptions={loadOptions}
        isValidNewOption={(input) => !!input}
        debounced={!readOnly}
        debounceTimeout={500}
        selectRef={selectRef}
        components={{
          Option: OptionMemoized,
        }}
        optionToString
        lockMenu={previewModalState.isOpen}
        noOptionsMessage={() => 'No properties found'}
        creatable={creatable}
        allowCreateWhileLoading
        validate={required ? validateRequired() : undefined}
      />

      <Modal noPadding size="lg" isOpen={previewModalState.isOpen} toClose={handleClosePreview}>
        {previewModalState.isOpen && <Preview pageId={previewModalState.pageId} />}
      </Modal>
    </>
  );
}

export function DetailsMapToMasterProperty({ isCreating }) {
  const isLoginAs = useSelector(isLoginAsSelector);
  const isAgent = useSelector(isAgentSelector);
  const isOwner = useSelector(isOwnerSelector);
  const { values } = useFormState();
  const masterPropRemoved = !isNil(values.masterPropertyId) && isNil(values.masterPropertyName);
  const isInReadOnly = isAgent && !isCreating && !isLoginAs && !masterPropRemoved;

  if (isOwner) {
    return null;
  }

  return <MapToMasterProperty readOnly={isInReadOnly} creatable={isAgent} required={isAgent} />;
}

export default MapToMasterProperty;
